/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import divorcePlannerStyle from "./divorcePlannerStyle";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import processInDetailImage from "../../assets/img/planner/icon_process_details_150px.png";
import ProcessStep from "./Process/ProcessStep";
import { Link } from "react-router";
import {
  STATUS_CHECKED,
  STATUS_COMPLETED,
  STATUS_DATE_SET,
  STATUS_DIVORCE_DONE,
  STATUS_DOCUMENTS_COMPLETE,
  STATUS_HEARING_PAID,
  STATUS_PROCESS_PAID,
  STATUS_SUBMITTED
} from "../../services/divorce/leadStatus";
import Person from "../../assets/icons/generic/IconPersonSmile";
import Court from "../../assets/icons/serviceOverview/IconCourt";
import Bird from "../../assets/icons/generic/IconBird";
import { getCaseLink } from "../../services/productService";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { Box } from "@mui/material";
import dayjs from "dayjs";

type DivorcePlannerProcessProps = {
  divorce: AbstractCase;
  id: string;
};

const DivorcePlannerProcess = ({ divorce, id }: DivorcePlannerProcessProps) => {
  if (!divorce) {
    return null;
  }
  const inSeparationYear = dayjs(divorce.dateSeparation).isAfter(dayjs().subtract(1, "year"));
  const shortMarriage = dayjs(divorce.dateMarriage).isAfter(dayjs().subtract(2, "year"));
  return (
    <Paper sx={divorcePlannerStyle.processContainer} elevation={4}>
      <Box id={id} sx={divorcePlannerStyle.scrollToAnchor} />
      <Box sx={divorcePlannerStyle.headlineProcess}>
        <Grid
          container
          sx={[{
            justifyContent: "center"
          }, divorcePlannerStyle.headlineContainer]}>
          <Grid size={12}>
            <Grid container sx={{
              alignItems: "center"
            }}>
              <Grid size={5}>
                <Box sx={divorcePlannerStyle.imageContainer}>
                  <Paper sx={divorcePlannerStyle.imageCircle}>
                    <img src={processInDetailImage} alt={"Ablauf"} />
                  </Paper>
                </Box>
              </Grid>
              <Grid size={7}>
                <Box sx={divorcePlannerStyle.contentName}>Ablauf im Detail</Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <ProcessStep
        icon={Person}
        checked={divorce.leadStatus >= STATUS_DOCUMENTS_COMPLETE}
        headline={"Heiratsurkunde hochladen"}
        text={
          <p>
            Um den Scheidungsantrag einreichen zu können, wird die Heiratsurkunde benötigt. Sie können die Urkunde ganz einfach in Ihrer{" "}
            <Link to={getCaseLink(divorce)}>Servicewelt</Link> hochladen
          </p>
        }
      />
      {divorce.numberChildren > 0 && (
        <ProcessStep
          icon={Person}
          checked={divorce.leadStatus >= STATUS_DOCUMENTS_COMPLETE}
          headline={(divorce.numberChildren === 1 ? "Geburtsurkunde" : "Geburtsurkunden") + " hochladen"}
          text={
            <p>
              Für das Einreichen des Antrags
              {divorce.numberChildren === 1
                ? " wird die Geburtsurkunde für Ihr gemeinsames, minderjähriges Kind "
                : " werden die Geburtsurkunden für Ihre gemeinsamen, minderjährigen Kinder "}
              benötigt. Sie können diese ganz einfach in Ihrer <Link to={getCaseLink(divorce)}>Servicewelt</Link> hochladen.
            </p>
          }
        />
      )}
      {inSeparationYear && (
        <ProcessStep
          icon={Person}
          checked={divorce.leadStatus >= STATUS_CHECKED}
          headline={"Voraussetzung für Scheidung sicherstellen: Das Trennungsjahr"}
          text={
            <p>
              Grundvoraussetzung dafür, dass ein Scheidungsantrag eingereicht werden kann, ist, dass Sie und Ihr Ehepartner ein Jahr getrennt voneinander leben.
              Was es hierbei genau zu beachten gibt, finden Sie in Ihrer <Link to={"#checkliste_trennung"}>Checkliste zum Trennungsjahr</Link>.
            </p>
          }
        />
      )}
      {divorce.hasProcessCostSupport && (
        <ProcessStep
          icon={Person}
          checked={divorce.leadStatus >= STATUS_CHECKED}
          headline={"Unterlagen für Antrag auf Verfahrenskostenhilfe"}
          text={
            <p>
              Der <Link to={getCaseLink(divorce) + "/vkh-assistent"}>Verfahrenskostenhilfe-Assistent</Link> hilft Ihnen dabei, das Formular bestmöglich
              auszufüllen und sorgt dafür, dass Sie an alle Belege denken.
            </p>
          }
        />
      )}
      {divorce.pensionsExcluded && !divorce.shortMarriage && (
        <>
          <ProcessStep
            icon={Person}
            checked={divorce.leadStatus >= STATUS_CHECKED}
            headline={"Versorgungsausgleich ausschließen"}
            text={<p>Sie vereinbaren zusammen mit Ihrem Ehepartner einen Termin bei einem Notar, um den Versorgungsausgleich ausschließen zu lassen.</p>}
          />
          <ProcessStep
            icon={Person}
            checked={divorce.leadStatus >= STATUS_CHECKED}
            headline={"Dokument zum Ausschluss des Versorgungsausgleichs hochladen"}
            text={
              <p>
                Das Dokument von Ihrem Notartermin können Sie ganz einfach in Ihrer <Link to={getCaseLink(divorce)}>Servicewelt</Link> hochladen.
              </p>
            }
          />
        </>
      )}
      <ProcessStep
        icon={Person}
        checked={divorce.leadStatus >= STATUS_CHECKED}
        headline={"Scheidungsfolgesachen mit Ehepartner klären"}
        text={
          <p>
            Im Ratgeber finden Sie weitere Informationen dazu, welche sogenannten{" "}
            <Link to={"/ratgeber/familienrecht/wichtige-themen-ueberblick"}>Scheidungsfolgesachen</Link> Sie mit Ihrem Partner regeln müssen. Auf Wunsch
            erstellen wir Ihnen eine Scheidungsfolgenvereinbarung zu den Themen Unterhalt, Vermögensaufteilung, Zugewinnausgleich und Sorgerecht.
          </p>
        }
      />
      <ProcessStep
        icon={Bird}
        checked={divorce.leadStatus >= STATUS_CHECKED}
        headline={"Ihr Scheidungsantrag wird erstellt"}
        text={<p>Wir erstellen Ihren Scheidungsantrag.</p>}
      />
      {divorce.paymentType === "private" && (
        <ProcessStep
          icon={Person}
          checked={divorce.leadStatus >= STATUS_PROCESS_PAID}
          headline={"Verfahrensgebühr bezahlen"}
          text={<p>Sie erhalten die Rechnung über die Verfahrensgebühr und bezahlen diese innerhalb von 14 Tagen.</p>}
        />
      )}
      <ProcessStep
        icon={Bird}
        checked={divorce.leadStatus >= STATUS_SUBMITTED}
        headline={"Ihr Scheidungsantrag wird bei Gericht eingereicht"}
        text={<p>Wir reichen Ihren Scheidungsantrag bei Gericht ein.</p>}
      />
      {divorce.hasProcessCostSupport && (
        <ProcessStep
          icon={Court}
          checked={divorce.leadStatus >= STATUS_DATE_SET}
          headline={"Das Gericht prüft Ihren Antrag auf Verfahrenskostenhilfe"}
          text={<p>Sobald der Antrag auf Verfahrenskostenhilfe bewilligt worden ist, bekommen Sie einen entsprechenden Bescheid.</p>}
        />
      )}
      {!divorce.hasProcessCostSupport && (
        <ProcessStep
          icon={Person}
          checked={divorce.leadStatus >= STATUS_DATE_SET}
          headline={"Gerichtskosten bezahlen"}
          text={
            <p>
              Das Gericht schickt Ihnen eine Rechnung für die Gerichtskosten, die Sie zeitnah bezahlen sollten, da das Gericht Ihre Scheidung vorher nicht
              bearbeitet.
            </p>
          }
        />
      )}
      <ProcessStep
        icon={Court}
        checked={divorce.leadStatus >= STATUS_DATE_SET}
        headline={"Der Scheidungsantrag wird Ihrem Ehepartner zugestellt"}
        text={<p>Das Gericht schickt den Scheidungsantrag an Ihren Ehepartner.</p>}
      />
      {!shortMarriage && !divorce.pensionsExcluded && (
        <>
          <ProcessStep
            icon={Person}
            checked={divorce.leadStatus >= STATUS_DATE_SET}
            headline={"Fragebogen zum Versorgungsausgleich ausfüllen"}
            text={
              <p>
                Sie und Ihr Ehepartner erhalten den Fragebogen zum sogenannten Versorgungsausgleich vom Gericht. Grundsätzlich führt das Gericht bei jeder
                Scheidung den Versorgungsausgleich durch. Das heißt, die während der Ehezeit erworbenen Ansprüche auf eine Altersversorgung (Rente) werden
                zwischen den Eheleuten ausgeglichen. Erst wenn der Versorgungsausgleich berechnet worden ist, legt das Gericht den Scheidungstermin fest. Im
                Ratgeber finden Sie weitere Informationen zum <Link to={"/scheidung/versorgungsausgleich"}>Versorgungsausgleich</Link>.
              </p>
            }
          />

          <ProcessStep
            icon={Court}
            checked={divorce.leadStatus >= STATUS_DATE_SET}
            headline={"Das Gericht berechnet den Versorgungsausgleich"}
            text={<p>Für die Berechnung des Versorgungsausgleich ist mit einer Dauer von ca. 3 bis 6 Monaten zu rechnen.</p>}
          />
        </>
      )}
      <ProcessStep
        icon={Court}
        checked={divorce.leadStatus >= STATUS_DATE_SET}
        headline={"Ihr Scheidungstermin wird festgelegt"}
        text={
          <p>
            Das Gericht bestimmt einen Scheidungstermin, zu dem Sie und Ihr Ehepartner verpflichtend erscheinen müssen. Hier finden Sie mehr Informationen zum{" "}
            <Link to={"/scheidung/gerichtstermin"}>Scheidungstermin</Link>.
          </p>
        }
      />
      {divorce.paymentType === "private" && (
        <ProcessStep
          icon={Person}
          checked={divorce.leadStatus >= STATUS_HEARING_PAID}
          headline={"Terminsgebühr bezahlen"}
          text={<p>Sie erhalten die Rechnung für die Terminsgebühr und bezahlen diese innerhalb von 14 Tagen.</p>}
        />
      )}
      <ProcessStep
        icon={Person}
        checked={divorce.leadStatus >= STATUS_DIVORCE_DONE}
        headline={"Scheidungstermin"}
        text={
          <p>
            Sie und Ihr Ehepartner nehmen den Gerichtstermin wahr. Sie werden hierbei von unseren Anwälten begleitet. Der Termin dauert in der Regel 10 bis 15
            Minuten.
          </p>
        }
      />
      <ProcessStep
        icon={Court}
        checked={divorce.leadStatus >= STATUS_COMPLETED}
        headline={"Vorläufiger Scheidungsbeschluss und Protokoll"}
        text={
          <p>
            Nach dem Scheidungstermin erhalten Sie den Scheidungsbeschluss sowie ein Protokoll des Termins. Der Scheidungsbeschluss ist vorläufig, da es noch
            eine Vier-Wochen Frist gibt, in der Sie oder Ihr Ehepartner Beschwerde gegen den Beschluss einlegen können.
          </p>
        }
      />
      <ProcessStep
        icon={Court}
        checked={divorce.leadStatus >= STATUS_COMPLETED}
        headline={"Sie sind geschieden - Scheidungsbeschluss mit Rechtskraftvermerk"}
        text={
          <p>
            Ca. 6 Wochen nach dem Scheidungstermin erhalten Sie den sogenannten Scheidungsbeschluss mit Rechtskraftvermerk von uns per Post im Original
            zugeschickt. Somit sind sind Sie rechtskräftig geschieden.
          </p>
        }
        hideConnector={true}
      />
    </Paper>
  );
};

export default DivorcePlannerProcess;
